import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "moment";
import { toast } from "react-toastify";
import axios from "../../utils/axios";
import { useAppDispatch } from "../../store/hooks";
import { openContainer } from "../../store/openContainerStore/openContainerThunk";

// Loose stock: open-container levels per item (and per batch) at one location,
// with Open / Adjust / Ledger on each card. Same rows the POS Loose Stock screen builds.

interface Props {
  locationId: string;
  locationName?: string;
  // Bumped by the parent after a container is opened from the header dialog.
  reloadKey?: number;
  // Item name or batch number typed in the header search box.
  search?: string;
}

interface IBatch {
  _id: string;
  batchNumber?: string;
  expiryDate?: string;
  onHand: number;
}

interface IRow {
  key: string;
  skuId: string;
  name: string;
  unit: string; // base unit name, e.g. MILLILITRE
  pack: string; // container unit code, e.g. BTL
  maxSize: number; // base units in one container
  batch: IBatch | null;
  batchCounters: { opened: number; drained: number; poured: number } | null;
  openRemaining: number;
  openContainers: any[];
  sealedContainers: number | null;
  containersOpened: number;
  containersDrained: number;
  containersSoldWhole: number;
  totalLooseSold: number;
}

const idOf = (v: any): string => (v && typeof v === "object" ? String(v._id || "") : v ? String(v) : "");
const fmt = (n: any) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};
const errorText = (e: any) => e?.response?.data?.message || e?.message || "Something went wrong";

const LEDGER_LABELS: Record<string, string> = {
  loose_sale: "Loose pour",
  bottle_open: "Container opened",
  bottle_drained: "Container drained",
  whole_sale: "Whole pack sold",
  adjustment: "Adjustment",
};

// Batches of an item that have stock at the location, nearest expiry first.
const getBatchesInStock = async (skuId: string, locationId: string): Promise<IBatch[]> => {
  const [batchRes, stockRes] = await Promise.all([
    axios.get(`/api/v1/erp/batches?skuId=${skuId}&isActive=true`),
    axios.get(`/api/v1/erp/stock/on-hand?skuId=${skuId}&locationId=${locationId}`),
  ]);
  const qty: Record<string, number> = {};
  (stockRes.data?.byBatch || []).forEach((r: any) => { if (r.batchId) qty[String(r.batchId)] = Number(r.qty) || 0; });
  return (Array.isArray(batchRes.data) ? batchRes.data : [])
    .filter((b: any) => (qty[String(b._id)] || 0) > 0)
    .map((b: any) => ({ ...b, onHand: qty[String(b._id)] }));
};

const LooseStock: FC<Props> = ({ locationId, locationName = "", reloadKey = 0, search = "" }) => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<IRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  const [openRow, setOpenRow] = useState<IRow | null>(null);
  const [adjustRow, setAdjustRow] = useState<IRow | null>(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [ledgerRow, setLedgerRow] = useState<IRow | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!locationId) {
      setRows([]);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const [stockRes, openRes, onHandRes] = await Promise.all([
          axios.get(`/api/v1/erp/open-containers/loose-stock?locationId=${locationId}`),
          axios.get(`/api/v1/erp/open-containers?locationId=${locationId}&status=open`).catch(() => ({ data: [] })),
          axios.get(`/api/v1/erp/stock/on-hand?locationId=${locationId}`).catch(() => ({ data: null })),
        ]);

        const openBySku: Record<string, any[]> = {};
        (Array.isArray(openRes.data) ? openRes.data : []).forEach((c: any) => {
          const id = idOf(c.skuId);
          if (id) (openBySku[id] = openBySku[id] || []).push(c);
        });
        const onHand: Record<string, number> | null = Array.isArray(onHandRes.data)
          ? Object.fromEntries(onHandRes.data.map((r: any) => [String(r.skuId), Number(r.totalQty) || 0]))
          : null;

        // Items with a loose pack, plus items with a container open even when no pack is saved on the item.
        const list = (Array.isArray(stockRes.data) ? stockRes.data : [])
          .filter((m: any) => (Number(m.containerSize) || 0) > 0 || (openBySku[idOf(m.skuId)] || []).length > 0)
          .map((m: any) => {
            if ((Number(m.containerSize) || 0) > 0) return m;
            const open = openBySku[idOf(m.skuId)];
            const size = Math.max(0, ...open.map((c: any) => Number(c.openedVolume) || 0));
            const unit = open.map((c: any) => c.containerUnit).find((u: any) => u && typeof u === "object");
            return { ...m, containerSize: size, containerUnit: m.containerUnit || unit || null };
          })
          .filter((m: any) => (Number(m.containerSize) || 0) > 0);

        const batchesBySku: Record<string, IBatch[]> = {};
        await Promise.all(
          list.map(async (m: any) => {
            const id = idOf(m.skuId);
            try {
              batchesBySku[id] = await getBatchesInStock(id, locationId);
            } catch {
              batchesBySku[id] = [];
            }
          })
        );

        const sumLeft = (cs: any[]) => cs.reduce((s, c) => s + (Number(c.remainingVolume) || 0), 0);
        const built: IRow[] = [];
        list.forEach((m: any) => {
          const skuId = idOf(m.skuId);
          const size = Number(m.containerSize) || 0;
          const containers = openBySku[skuId] || [];
          const base = {
            skuId,
            name: m.skuId?.name || "Item",
            unit: m.baseUnitName || "",
            pack: m.containerUnit?.code || m.containerUnit?.name || "container",
            maxSize: size,
            containersOpened: Number(m.containersOpened) || 0,
            containersDrained: Number(m.containersDrained) || 0,
            containersSoldWhole: Number(m.containersSoldWhole) || 0,
            totalLooseSold: Number(m.totalLooseSold) || 0,
          };
          const batches = batchesBySku[skuId] || [];
          if (!batches.length) {
            // Sealed = stock not sitting in an open container, in whole containers.
            const sealedVolume = onHand ? (onHand[skuId] || 0) - (Number(m.openRemaining) || 0) : null;
            built.push({
              ...base,
              key: skuId,
              batch: null,
              batchCounters: null,
              openRemaining: Number(m.openRemaining) || 0,
              openContainers: containers,
              sealedContainers: sealedVolume == null ? null : sealedVolume <= 0 ? 0 : Math.floor(sealedVolume / size + 1e-9),
            });
            return;
          }
          const batchIds = new Set(batches.map((b) => b._id));
          batches.forEach((b) => {
            const mine = containers.filter((c: any) => idOf(c.batchId) === b._id);
            const counters = m.batchCounters?.[b._id] || { opened: 0, drained: 0, poured: 0 };
            built.push({ ...base, key: `${skuId}-${b._id}`, batch: b, batchCounters: counters, openRemaining: sumLeft(mine), openContainers: mine, sealedContainers: null });
          });
          // Containers opened without a batch (or whose batch ran out) keep a row of their own.
          const rest = containers.filter((c: any) => !batchIds.has(idOf(c.batchId)));
          if (rest.length) built.push({ ...base, key: `${skuId}-rest`, batch: null, batchCounters: null, openRemaining: sumLeft(rest), openContainers: rest, sealedContainers: null });
        });

        if (active) setRows(built);
      } catch (e: any) {
        if (active) setError(errorText(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [locationId, tick, reloadKey]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q) || (r.batch?.batchNumber || "").toLowerCase().includes(q));
  }, [rows, search]);

  const rowTitle = (r: IRow) => (r.batch ? `${r.name} · Batch ${r.batch.batchNumber || "-"}` : r.name);

  const confirmOpen = async () => {
    if (!openRow) return;
    setSaving(true);
    try {
      await dispatch(openContainer({ skuId: openRow.skuId, locationId, batchId: openRow.batch?._id })).unwrap();
      setOpenRow(null);
      reload();
    } catch {
      /* toast handled in slice */
    } finally {
      setSaving(false);
    }
  };

  const applyAdjust = async () => {
    if (!adjustRow) return;
    const delta = Number(adjustDelta);
    if (!delta) {
      toast.error("Enter a non-zero amount");
      return;
    }
    setSaving(true);
    try {
      await axios.post("/api/v1/erp/open-containers/adjust", {
        skuId: adjustRow.skuId,
        locationId,
        qtyDelta: delta,
        batchId: adjustRow.batch?._id || undefined,
        note: adjustNote.trim() || undefined,
      });
      toast.success("Stock adjusted");
      setAdjustRow(null);
      reload();
    } catch (e: any) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  // Sealed containers of a row: the batch's stock not sitting in its open container, in whole packs.
  const sealedOf = (r: IRow) => {
    if (r.batch) {
      const stock = r.batch.onHand;
      return r.maxSize > 0 ? (stock <= r.openRemaining ? 0 : Math.floor((stock - r.openRemaining) / r.maxSize + 1e-9)) : 0;
    }
    return r.sealedContainers;
  };

  const actionButton = (title: string, icon: JSX.Element, onClick: () => void) => (
    <Tooltip title={title} arrow>
      <IconButton size="small" onClick={onClick} style={{ color: "#488dff", backgroundColor: "#f5f5f5", height: 28, width: 28, marginRight: 8 }}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Item",
        flex: 1.4,
        minWidth: 180,
        renderCell: ({ row }: GridValueGetterParams) => (
          <Box sx={{ overflow: "hidden" }}>
            <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
            {row.batch && <div style={{ fontSize: 11, color: "#6b7280" }}>Batch {row.batch.batchNumber || "-"}{row.batch.expiryDate ? ` · Exp ${moment(row.batch.expiryDate).format("DD MMM YYYY")}` : ""}</div>}
          </Box>
        ),
      },
      {
        field: "openRemaining",
        headerName: "Remaining / Opened",
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        valueGetter: ({ row }: GridValueGetterParams) => {
          const r = row as IRow;
          const open = r.openContainers.filter((c) => (Number(c.remainingVolume) || 0) > 0);
          // One open container per row normally; several (no batch) show their total against one pack.
          const size = open.length === 1 ? Number(open[0].openedVolume) || r.maxSize : r.maxSize;
          return `${fmt(r.openRemaining)} / ${fmt(size)} ${r.unit}${open.length > 1 ? ` (${open.length} open)` : ""}`;
        },
      },
      {
        field: "stock",
        headerName: "Stock",
        flex: 1,
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => {
          const r = row as IRow;
          const sealed = sealedOf(r);
          return (
            <Box>
              {r.batch && <div>{fmt(r.batch.onHand)} {r.unit}</div>}
              {sealed != null && <div style={{ fontSize: 11, color: "#6b7280" }}>{sealed} sealed {r.pack}</div>}
              {!r.batch && sealed == null && <span>-</span>}
            </Box>
          );
        },
      },
      {
        field: "opened",
        headerName: "Opened",
        width: 110,
        valueGetter: ({ row }: GridValueGetterParams) => (row.batchCounters ? row.batchCounters.opened : row.containersOpened),
      },
      {
        field: "drained",
        headerName: "Drained",
        width: 110,
        valueGetter: ({ row }: GridValueGetterParams) => (row.batchCounters ? row.batchCounters.drained : row.containersDrained),
      },
      {
        field: "soldWhole",
        headerName: "Sold whole",
        width: 120,
        valueGetter: ({ row }: GridValueGetterParams) => (row.batchCounters ? "-" : row.containersSoldWhole),
      },
      {
        field: "poured",
        headerName: "Poured",
        flex: 0.8,
        minWidth: 120,
        valueGetter: ({ row }: GridValueGetterParams) => `${fmt(row.batchCounters ? row.batchCounters.poured : row.totalLooseSold)} ${row.unit}`,
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) =>
          row.openRemaining > 0 && row.maxSize > 0
            ? <Chip size="small" style={{ borderRadius: 7, color: "#49a563", backgroundColor: "#edf6ef" }} label="Open" />
            : <Chip size="small" style={{ borderRadius: 7, color: "#6b7280", backgroundColor: "#f3f4f6" }} label="Not open" />,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => {
          const r = row as IRow;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {actionButton(`Open ${r.pack}`, <AddCircleOutlineIcon style={{ fontSize: 17 }} />, () => setOpenRow(r))}
              {actionButton("Adjust", <TuneIcon style={{ fontSize: 17 }} />, () => { setAdjustRow(r); setAdjustDelta(""); setAdjustNote(""); })}
              {actionButton("Ledger", <HistoryIcon style={{ fontSize: 17 }} />, () => setLedgerRow(r))}
            </Box>
          );
        },
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box>
      {!locationId ? (
        <Typography sx={{ color: "#6b7280", py: 4, textAlign: "center" }}>Select a location to see its loose stock.</Typography>
      ) : loading && rows.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={28} /></Box>
      ) : error ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#c33b3b", mb: 1 }}>Failed to load: {error}</Typography>
          <Button variant="outlined" size="small" onClick={reload}>Retry</Button>
        </Box>
      ) : visible.length === 0 ? (
        <Typography sx={{ color: "#6b7280", py: 4, textAlign: "center" }}>
          {search.trim() ? `No open container matching "${search.trim()}"` : "No items sold loose at this location yet. Open a container to start."}
        </Typography>
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid columns={columns} rows={visible} rowCount={visible.length} rowsPerPageOptions={[25, 50, 100]} getRowId={(row: any) => row.key} loading={loading} />
        </div>
      )}

      {/* Open a fresh container for this row's item and batch */}
      <Dialog open={!!openRow} onClose={() => !saving && setOpenRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Open a fresh container?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>Start a new full container for "{openRow ? rowTitle(openRow) : ""}" for loose pouring.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRow(null)} disabled={saving}>Cancel</Button>
          <LoadingButton variant="contained" loading={saving} onClick={confirmOpen}>Open</LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Adjust the open container's level */}
      <Dialog open={!!adjustRow} onClose={() => !saving && setAdjustRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust open container</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 2 }}>
            Add or remove {adjustRow?.unit} from the open container for "{adjustRow ? rowTitle(adjustRow) : ""}". Use a negative value for wastage/spillage.
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={`Change (${adjustRow?.unit || ""})  e.g. -30`}
            value={adjustDelta}
            onChange={(e) => setAdjustDelta(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField fullWidth size="small" label="Reason / note" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustRow(null)} disabled={saving}>Cancel</Button>
          <LoadingButton variant="contained" loading={saving} onClick={applyAdjust}>Apply</LoadingButton>
        </DialogActions>
      </Dialog>

      <LedgerDrawer row={ledgerRow} locationId={locationId} locationName={locationName} onClose={() => setLedgerRow(null)} />
    </Box>
  );
};

// ─── Ledger drawer ────────────────────────────────────────────────────────────

interface LedgerProps {
  row: IRow | null;
  locationId: string;
  locationName: string;
  onClose: () => void;
}

const LedgerDrawer: FC<LedgerProps> = ({ row, locationId, locationName, onClose }) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    let active = true;
    setLoading(true);
    setError("");
    axios
      .get(`/api/v1/erp/open-containers/ledger?skuId=${row.skuId}&locationId=${locationId}`)
      .then((res) => {
        if (!active) return;
        const list: any[] = Array.isArray(res.data) ? res.data : [];
        // Batch of a row: its own, else its container's. Older pours carry no batch and take it from a container row on the same bill.
        const batchOf = (e: any) => e.batchId || (e.containerId && typeof e.containerId === "object" ? e.containerId.batchId : null);
        const batchByRef: Record<string, any> = {};
        list.forEach((e) => { if (e.refId && batchOf(e)) batchByRef[String(e.refId)] = batchOf(e); });
        const mapped = list.map((e) => {
          const batch = batchOf(e) || batchByRef[String(e.refId || "")];
          return { ...e, batchKey: idOf(batch), batchNumber: batch && typeof batch === "object" ? batch.batchNumber : null };
        });
        // Rows with no batch at all (old whole-pack sales) cannot be placed, so they stay visible.
        setEntries(row.batch ? mapped.filter((e) => !e.batchKey || e.batchKey === row.batch!._id) : mapped);
      })
      .catch((e) => { if (active) setError(errorText(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [row, locationId]);

  const qty = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));

  const entry = (e: any, i: number) => {
    const delta = Number(e.qtyDelta) || 0;
    const openedSize = e.action === "bottle_open" ? Number(e.openRemainingAfter) || 0 : 0;
    const openedText = openedSize <= 0 || !row
      ? ""
      : `${openedSize === row.maxSize ? `1 ${row.pack} · ` : ""}${qty(openedSize)} ${row.unit}${e.batchNumber ? ` · Batch ${e.batchNumber}` : ""}`;
    const meta = [e.locationId?.name || locationName, e.actorId?.name, e.createdAt ? moment(e.createdAt).format("DD MMM YYYY, hh:mm A") : ""].filter(Boolean).join(" · ");
    const ref = [e.variantName, e.refNumber ? `Bill #${e.refNumber}` : ""].filter(Boolean).join(" · ");
    return (
      <Box key={e._id || i}>
        {i > 0 && <Divider sx={{ my: 1.5 }} />}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{LEDGER_LABELS[e.action] || e.action}</div>
            {openedText && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{openedText}</div>}
            {ref && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{ref}</div>}
            {e.note && <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", marginTop: 2 }}>{e.note}</div>}
            {meta && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>{meta}</div>}
          </Box>
          {delta !== 0 && (
            <span style={{ fontSize: 13, fontWeight: 600, color: delta < 0 ? "#c33b3b" : "#059669" }}>
              {delta > 0 ? "+" : ""}{qty(delta)} {row?.unit}
            </span>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Drawer anchor="right" open={!!row} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 2, borderBottom: "1px solid #dcdcdc" }}>
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 600 }}>
          {row ? (row.batch ? `${row.name} · Batch ${row.batch.batchNumber || "-"}` : row.name) : ""} — ledger{locationName ? ` · ${locationName}` : ""}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ p: 2.5, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
        ) : error ? (
          <Typography sx={{ color: "#c33b3b" }}>Failed: {error}</Typography>
        ) : entries.length === 0 ? (
          <Typography sx={{ color: "#9ca3af", textAlign: "center", py: 4 }}>No activity yet</Typography>
        ) : (
          entries.map(entry)
        )}
      </Box>
    </Drawer>
  );
};

export default LooseStock;
